export interface ReconnectDecision {
  attempt: number;
  delayMs: number;
  firstAtMaximumDelay: boolean;
}

/** Tracks consecutive reconnect failures while allowing recovery to continue indefinitely. */
export class RealtimeReconnectPolicy {
  private attempts = 0;

  constructor(
    private readonly baseDelayMs: number,
    private readonly maximumDelayMs: number,
    /** Injectable for tests; full-jitter uses U[0, nominal]. */
    private readonly random: () => number = () => Math.random(),
  ) {}

  get attemptCount(): number {
    return this.attempts;
  }

  next(): ReconnectDecision {
    const previousNominal = this.nominalDelayFor(this.attempts);
    this.attempts += 1;
    const nominalDelayMs = this.nominalDelayFor(this.attempts);
    return {
      attempt: this.attempts,
      delayMs: this.applyFullJitter(nominalDelayMs),
      firstAtMaximumDelay:
        nominalDelayMs === this.maximumDelayMs && previousNominal < this.maximumDelayMs,
    };
  }

  reset(): void {
    this.attempts = 0;
  }

  private nominalDelayFor(attempt: number): number {
    if (attempt <= 0) return 0;
    const maximumExponent = Math.ceil(Math.log2(this.maximumDelayMs / this.baseDelayMs));
    const exponent = Math.min(attempt - 1, maximumExponent);
    return Math.min(this.baseDelayMs * (2 ** exponent), this.maximumDelayMs);
  }

  /** Full jitter: spread mass reconnects across [0, nominal] instead of lockstep. */
  private applyFullJitter(nominalDelayMs: number): number {
    if (nominalDelayMs <= 0) return 0;
    return Math.floor(this.random() * (nominalDelayMs + 1));
  }
}
